"use client";

import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import SingleSelect from "@/components/ui/single-select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Article } from "@/types/article";
import { useSeriesStore } from "@/stores/seriesStore";
import { articleService } from "@/services/articleService";
import { showToast } from "@/lib/toast";

interface ModalBulkAssignSeriesProps {
  isOpen: boolean;
  onClose: () => void;
  selectedArticles: Article[];
  onCompleted: () => void;
}

export default function ModalBulkAssignSeries({ isOpen, onClose, selectedArticles, onCompleted }: ModalBulkAssignSeriesProps) {
  const { seriesList, fetchSeries } = useSeriesStore();
  const [seriesId, setSeriesId] = useState<string>("");
  const [assignments, setAssignments] = useState<{ id: string; partNumber: string | number }[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSeries({ page: 1, limit: 1000 });
      // Gợi ý đánh số 1..n theo thứ tự hiện tại
      setAssignments(selectedArticles.map((a, idx) => ({ id: a._id, partNumber: String(idx + 1) })));
      setSeriesId("");
    }
  }, [isOpen, selectedArticles, fetchSeries]);

  const seriesOptions = useMemo(() => seriesList.map(s => ({ label: `${s.name} (${s.key})`, value: s._id })), [seriesList]);
  const selectedSeries = useMemo(() => seriesList.find(s => s._id === seriesId) || null, [seriesList, seriesId]);

  const canSubmit = !!selectedSeries && assignments.length > 0 && assignments.every(x => {
    const partStr = String(x.partNumber || "").trim();
    return partStr !== "";
  });

  const handleSubmit = async () => {
    if (!selectedSeries) return;
    setIsSubmitting(true);
    try {
      const payload = {
        seriesKey: selectedSeries.key,
        items: assignments.map(a => ({ id: a.id, partNumber: a.partNumber })),
      };
      const res = await articleService.bulkAssignSeries(payload);
      if (res.ok) {
        showToast.success("Thành công", "Gán series cho phim đã hoàn tất");
        onCompleted();
        onClose();
      } else {
        showToast.error("Lỗi", (res as any)?.data?.message || "Không thể gán series");
      }
    } catch (e: any) {
      showToast.error("Lỗi", e?.message || "Không thể gán series");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Gán series cho {selectedArticles.length} phim</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Chọn series</Label>
            <SingleSelect
              options={seriesOptions}
              value={seriesId}
              onChange={setSeriesId}
              placeholder="Chọn series"
            />
          </div>

          <div className="border rounded-lg">
            <div className="grid grid-cols-12 p-3 border-b bg-gray-50 text-sm font-semibold">
              <div className="col-span-7">Phim</div>
              <div className="col-span-5">Tên phần</div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {assignments.map((a, idx) => {
                const article = selectedArticles.find(x => x._id === a.id);
                if (!article) return null;
                return (
                  <div key={a.id} className="grid grid-cols-12 items-center gap-3 p-3 border-b last:border-b-0">
                    <div className="col-span-7 truncate text-sm">
                      {(article as any).movieData?.name || article.slug}
                    </div>
                    <div className="col-span-5">
                      <Input
                        type="text"
                        value={a.partNumber}
                        onChange={(e) => {
                          const v = e.target.value;
                          setAssignments((prev) => prev.map((it, i) => i === idx ? { ...it, partNumber: v } : it));
                        }}
                        placeholder="Ví dụ: 1, Phần 1, Tập 1"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>Hủy</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? "Đang lưu..." : "Gán series"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


